import React, { useState } from "react";
import { Breadcrumb } from "../../components/ui";
import { useParams, useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import toast from "react-hot-toast";
import {
  useGetTenantDomainsQuery,
  useAddTenantDomainMutation,
  useRemoveTenantDomainMutation,
  useSetPrimaryTenantDomainMutation,
  TenantDomain,
} from "../../store/services/tenantDomains";

// MT-013 — CRUD de dominios por tenant. Solo SUPER_ADMIN (el backend lo enforcea).
export const TenantDomains = () => {
  const { tenantId = "" } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useGetTenantDomainsQuery(tenantId, { skip: !tenantId });
  const [addDomain, { isLoading: isAdding }] = useAddTenantDomainMutation();
  const [removeDomain] = useRemoveTenantDomainMutation();
  const [setPrimary] = useSetPrimaryTenantDomainMutation();

  const [newDomain, setNewDomain] = useState("");

  const handleAdd = async () => {
    if (!newDomain.trim()) return;
    try {
      await addDomain({ tenantId, domain: newDomain.trim() }).unwrap();
      setNewDomain("");
      toast.success("Dominio agregado");
    } catch (e: any) {
      toast.error(e?.data?.message || "No se pudo agregar el dominio");
    }
  };

  const handleRemove = async (domain: TenantDomain) => {
    try {
      await removeDomain({ tenantId, domainId: domain.id }).unwrap();
      toast.success("Dominio desactivado");
    } catch {
      toast.error("No se pudo desactivar el dominio");
    }
  };

  const handleSetPrimary = async (domain: TenantDomain) => {
    try {
      await setPrimary({ tenantId, domainId: domain.id }).unwrap();
      toast.success("Dominio marcado como primario");
    } catch {
      toast.error("No se pudo marcar como primario");
    }
  };

  if (isLoading) return <div className="p-4">Cargando…</div>;

  return (
    <div className="p-4">
      <Breadcrumb items={[{ label: "Tenants", to: "/tenants" }, { label: "Dominios" }]} />
      <div className="flex align-items-center gap-2 mb-3">
        <Button icon="pi pi-arrow-left" text onClick={() => navigate("/tenants")} />
        <h2 className="m-0">Dominios del tenant</h2>
      </div>

      {data?.isDefaultTenant ? (
        <Message
          severity="info"
          text="Este tenant es el default. Los usuarios sin dominio institucional se asignan aquí automáticamente."
        />
      ) : (
        <>
          <div className="flex gap-2 mb-4">
            <InputText
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="ejemplo.edu.ar"
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            />
            <Button label="Agregar" icon="pi pi-plus" onClick={handleAdd} loading={isAdding} />
          </div>

          <DataTable value={data?.domains ?? []} emptyMessage="Sin dominios">
            <Column field="domain" header="Dominio" />
            <Column
              header="Primario"
              body={(row: TenantDomain) =>
                row.isPrimary ? <Tag value="Primario" severity="success" /> : null
              }
            />
            <Column
              header="Estado"
              body={(row: TenantDomain) => (
                <Tag value={row.active ? "Activo" : "Inactivo"} severity={row.active ? "info" : "danger"} />
              )}
            />
            <Column
              header="Acciones"
              body={(row: TenantDomain) => (
                <div className="flex gap-2">
                  {row.active && !row.isPrimary && (
                    <Button
                      label="Hacer primario"
                      size="small"
                      text
                      onClick={() => handleSetPrimary(row)}
                    />
                  )}
                  {row.active && (
                    <Button
                      icon="pi pi-trash"
                      size="small"
                      severity="danger"
                      text
                      onClick={() => handleRemove(row)}
                    />
                  )}
                </div>
              )}
            />
          </DataTable>
        </>
      )}
    </div>
  );
};
